import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { makeEmployeeClient } from "./employeeApi";

const KEY = "msh_employee_system_token";
const TAB_KEY = "msh_employee_system_tab";

// Separate from AuthContext: never read, replace or remove the administrator's tokens.
export const useEmployeeSession = () => {
    const http = useMemo(() => axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } }), []);
    const client = useMemo(() => makeEmployeeClient(http), [http]);
    const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
    const [profile, setProfile] = useState(null);
    const [checking, setChecking] = useState(!!token);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);

    const logout = useCallback(() => {
        localStorage.removeItem(KEY);
        sessionStorage.removeItem(TAB_KEY);
        setToken("");
        setProfile(null);
        setChecking(false);
        setError("");
    }, []);

    useEffect(() => {
        // Old demo credentials are deliberately NOT migrated into the real system.
        sessionStorage.removeItem("msh_demo_token");
        const request = http.interceptors.request.use((config) => {
            const current = localStorage.getItem(KEY);
            if (current && config.url !== "/auth/login") config.headers.Authorization = `Bearer ${current}`;
            return config;
        });
        const response = http.interceptors.response.use((r) => r, (err) => {
            const current = localStorage.getItem(KEY);
            const unauthorized = err.response?.status === 401 ||
                (err.response?.status === 403 && err.response?.data?.detail === "tenant_inactive");
            if (unauthorized && current && err.config?.headers?.Authorization === `Bearer ${current}`) {
                logout();
                setError("Sessionen er udløbet, eller kontoen er ikke aktiv. Log ind igen.");
            }
            return Promise.reject(err);
        });
        return () => { http.interceptors.request.eject(request); http.interceptors.response.eject(response); };
    }, [http, logout]);

    useEffect(() => {
        if (!token) return;
        let alive = true;
        setChecking(true);
        client.getProfile().then((data) => { if (alive) { setProfile(data); setError(""); } })
            .catch((err) => { if (alive) setError(formatApiError(err)); })
            .finally(() => { if (alive) setChecking(false); });
        return () => { alive = false; };
    }, [client, token, retry]);

    const login = async (email, password) => {
        const { data } = await http.post("/auth/login", { email: email.trim(), password });
        if (!data.token) throw new Error("Login kunne ikke gennemføres.");
        sessionStorage.removeItem(TAB_KEY);
        localStorage.setItem(KEY, data.token);
        setError("");
        setChecking(true);
        setToken(data.token);
    };

    return { client, token, profile, checking, error, login, logout, retry: () => setRetry((n) => n + 1), tabKey: TAB_KEY };
};